export function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export function isImageExtension(extension: string): boolean {
  // https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types
  const imageExtensions: string[] = [
    'apng',
    'avif',
    'gif',
    'jpg',
    'jpeg',
    'jfif',
    'pjpeg',
    'pjp',
    'png',
    'svg',
    'tif',
    'tiff',
    'webp',
  ];
  return imageExtensions.includes(extension);
}

export function mapMimeTypeToExtension(mimeTypeOrExtension: string): string {
  switch (mimeTypeOrExtension) {
    case 'image/gif':
      return '.gif';
    case 'image/jpg':
      return '.jpg';
    case 'image/jpeg':
      return '.jpeg';
    case 'image/png':
      return '.png';
    case 'image/svg+xml':
      return '.svg';
    case 'video/3gpp':
      return '.3gp';
    case 'video/mp4':
      return '.mp4';
    case 'video/mpeg':
      return '.mpeg';
    case 'video/quicktime':
      return '.mov';
    case 'video/x-ms-wmv':
      return '.wmv';
    default:
      // treat as extension if not matched
      return mimeTypeOrExtension;
  }
}

/**
 * Function makes sure that both .jpeg and .jpg plus .mpeg and .mpg extensions are provided if at least one of them is available
 * Returned list is sorted and has unique values
 */
export function sanitizeExtensions(extensions: string[]): string[] {
  let sanitizedExtensions = extensions.map((ext) => ext.toLocaleLowerCase());

  const hasJpeg = sanitizedExtensions.some((ext) => ext === '.jpeg' || ext === '.jpg');
  const hasMpeg = sanitizedExtensions.some((ext) => ext === '.mpeg' || ext === '.mpg');

  sanitizedExtensions = [...sanitizedExtensions]
    .filter((ext) => ext !== '.jpeg' && ext !== '.jpg')
    .filter((ext) => ext !== '.mpeg' && ext !== '.mpg');

  if (hasJpeg) {
    sanitizedExtensions.push('.jpg', '.jpeg');
  }

  if (hasMpeg) {
    sanitizedExtensions.push('.mpg', '.mpeg');
  }

  return [...new Set(sanitizedExtensions)].sort((a, b) => a.localeCompare(b)); // sorted unique values
}

/**
 * Gets user-friendly file type. In most cases it is the file extension.
 * @param name name of the file
 */
export function getFriendlyFileType(name: string): string | undefined {
  const nameParts = name.split('.');
  return nameParts.length > 1 ? nameParts.pop()?.toLocaleLowerCase() : undefined;
}

export const DEFAULT_MIME_TYPE = 'application/octet-stream';

export function mapToMimeType(data?: File | Blob, fallbackName?: string): string {
  const name = data && 'name' in data ? data.name : fallbackName || '';
  const extension = name.toLocaleLowerCase().split('.').pop();
  const mimeType = mapExtensionToMimeType(extension);
  return mimeType || data?.type || DEFAULT_MIME_TYPE;
}

export function mapExtensionToMimeType(extension?: string): string | undefined {
  switch (extension?.replace('.', '')) {
    // images
    case 'gif':
      return 'image/gif';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'svg':
      return 'image/svg+xml';
    // video
    case 'mov':
      return 'video/quicktime';
    case 'mp4':
      return 'video/mp4';
    case 'mpg':
    case 'mpeg':
      return 'video/mpeg';
    case 'webm':
      return 'video/webm';
    case 'wmv':
      return 'video/x-ms-wmv';
    // audio
    case 'flac':
      return 'audio/flac';
    case 'm4a':
      return 'audio/mp4';
    case 'mp3':
      return 'audio/mpeg';
    case 'mpga':
      return 'audio/mpeg';
    case 'ogg':
      return 'audio/ogg';
    case 'wav':
      return 'audio/wav';
    // documents
    case 'pdf':
      return 'application/pdf';
    case 'csv':
      return 'text/csv';
    case 'doc':
      return 'application/msword';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return undefined;
  }
}
